import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Draggable, type DraggableProvided, type DraggableStateSnapshot } from '@hello-pangea/dnd';
import { Box, Divider, IconButton, Stack, useTheme } from '@mui/material';
import _get from 'lodash/get';

import { FullTalentQuestionDTO } from '@/api/generated';
import CopyIcon from '@/assets/icons/copy.svg?react';
import DeleteIcon from '@/assets/icons/delete.svg?react';
import DragIcon from '@/assets/icons/drag.svg?react';
import DropdownSelect from '@/components/fields/DropdownSelect';
import Field from '@/components/fields/Field';
import TextInput from '@/components/fields/TextInput';
import ToggleSwitch from '@/components/fields/ToggleSwitch';
import { useDirection } from '@/hooks/useDirection';
import { useFieldPrefix } from '@/hooks/useFieldPrefix';
import { TALENT_REGISTRATION_FORM_TABS } from '@/root/linker';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';

import { DEFAULT_LANGUAGE_CODE, MULTI_OPTION_TYPES, OTHER_OPTION_ID } from './constants';
import { getQuestionTypes } from './options';
import TalentRegistrationFormQuestionOption from './TalentRegistrationFormMultiOption';

const QUESTION_TYPE_WIDTH = 256;

interface TalentRegistrationFormQuestionProps {
  activeTab: string;
  activeLanguage: string;
  index: number;
  question: FullTalentQuestionDTO;
  questionKey: string;
  onCopyQuestion: (questionId: string) => void;
  onRemoveQuestionRequest: (questionId: string) => void;
  onAddQuestionOption: (questionId: string, isOther?: boolean) => void;
  onRemoveQuestionOption: (questionId: string, optionId: string) => void;
  provided?: DraggableProvided;
  snapshot?: DraggableStateSnapshot;
  disabled?: boolean;
}

export const TalentRegistrationFormQuestion = ({
  activeTab,
  activeLanguage,
  index,
  question,
  onCopyQuestion,
  onRemoveQuestionRequest,
  onAddQuestionOption,
  onRemoveQuestionOption,
  provided,
  snapshot,
  disabled
}: TalentRegistrationFormQuestionProps) => {
  const { spacing } = useTheme();
  const { t } = useTranslation('settings');
  const { directionalSpacing } = useDirection();
  const { setValue, watch } = useFormContext();
  const { getPrefixedFieldName } = useFieldPrefix();
  const questionId = question?.id;
  // const isExternal = activeTab === TALENT_REGISTRATION_FORM_TABS.EXTERNAL;

  const questionTypes = useMemo(() => getQuestionTypes(t /*, isExternal */), [t /*, isExternal */]);

  const onUpdateOptions = useCallback(
    (options: string[]) => {
      setValue(getPrefixedFieldName(`${activeTab}.${index}.optionValues`), options, { shouldDirty: true });
    },
    [activeTab, getPrefixedFieldName, index, setValue]
  );
  const translations = watch(getPrefixedFieldName('translations'));
  const labelPlaceholder = useMemo(() => {
    const otherLanguages = Object.keys(translations || {}).filter(
      lang => lang !== DEFAULT_LANGUAGE_CODE && lang !== activeLanguage
    );
    const existingDefaultTranslation = _get(translations, `${DEFAULT_LANGUAGE_CODE}.${questionId}.label`);
    const fallbackTranslation = otherLanguages
      .map(lang => _get(translations, `${lang}.${questionId}.label`))
      .find(Boolean);

    return (
      existingDefaultTranslation || fallbackTranslation || t('orgSettings.talentRegistrationForm.questionPlaceholder')
    );
  }, [translations, questionId, t, activeLanguage]);

  if (!question?.isActive) return null;

  const isMultiOption = MULTI_OPTION_TYPES?.includes(question.type!);
  const isOtherEnabled = !question.optionValues?.includes(OTHER_OPTION_ID);

  return (
    <Stack
      {...provided?.draggableProps}
      ref={provided?.innerRef}
      border={borders.half}
      borderColor="grey.400"
      p={2}
      pt={0.5}
      mb={1}
      borderRadius={spacing(0.5)}
      bgcolor="background.default"
      width="100%"
      sx={{
        ...provided?.draggableProps?.style,
        userSelect: 'none',
        opacity: snapshot?.isDragging ? 0.6 : 1,
        bgcolor: snapshot?.isDragging ? 'primary.100' : 'background.default'
      }}
    >
      {!disabled && (
        <Stack direction="row" justifyContent="center" mb={1}>
          <Box sx={{ bgcolor: 'transparent' }} {...provided?.dragHandleProps}>
            <DragIcon />
          </Box>
        </Stack>
      )}
      <Stack
        direction="row"
        sx={{
          mt: disabled ? 1 : 0,
          mb: 1,
          '& .MuiFormControl-root': {
            p: 0,
            mb: 0
          }
        }}
      >
        <Field
          component={TextInput}
          name={`translations.${activeLanguage}.${questionId}.label`}
          innerProps={{
            sx: {
              flexGrow: 1,
              pb: 0,
              mb: 2,
              '&.MuiTextField-root .MuiInputBase-input': {
                p: directionalSpacing(1.25, 0.5, 1.25, 1),
                ...typography.body2
              }
            },
            placeholder: labelPlaceholder,
            disabled: disabled
          }}
        />
        <Field
          component={DropdownSelect}
          name={`${activeTab}.${index}.type`}
          innerProps={{
            options: questionTypes,
            isMulti: false,
            isSearchable: false,
            usePortal: true,
            isDisabled: disabled,
            sx: {
              width: QUESTION_TYPE_WIDTH,
              px: 0.5,
              '& .DropdownSelect-control': { minHeight: spacing(5) },
              '& .DropdownSelect-optionIcon': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }
            }
          }}
        />
      </Stack>
      {isMultiOption && (
        <TalentRegistrationFormQuestionOption
          activeLanguage={activeLanguage}
          questionId={questionId!}
          options={question.optionValues}
          isOtherEnabled={isOtherEnabled}
          onAddQuestionOption={onAddQuestionOption}
          onRemoveQuestionOption={onRemoveQuestionOption}
          onUpdateOptions={onUpdateOptions}
          questionType={question.type!}
        />
      )}
      <Divider orientation="horizontal" sx={{ borderColor: 'grey.400', mx: -2, mb: 2 }} />
      <Stack direction="row" justifyContent="end" gap={2}>
        {!disabled && (
          <Stack direction="row" gap={1} alignItems="center">
            <IconButton
              title={t('orgSettings.talentRegistrationForm.copy')}
              onClick={() => onCopyQuestion(questionId!)}
              sx={{ p: 0 }}
            >
              <CopyIcon />
            </IconButton>
            <IconButton
              title={t('orgSettings.talentRegistrationForm.delete')}
              onClick={() => onRemoveQuestionRequest(questionId!)}
              sx={{ p: 0 }}
            >
              <DeleteIcon />
            </IconButton>
            {activeTab === TALENT_REGISTRATION_FORM_TABS.EXTERNAL && (
              <Divider orientation="vertical" sx={{ height: spacing(2) }} />
            )}
          </Stack>
        )}
        <Stack direction="row" gap={2.5}>
          {activeTab === TALENT_REGISTRATION_FORM_TABS.EXTERNAL && (
            <>
              <Field
                component={ToggleSwitch}
                name={`${activeTab}.${index}.isRequired`}
                label={t('orgSettings.talentRegistrationForm.required')}
                innerProps={{
                  sx: { pointerEvents: disabled ? 'none' : 'auto' }
                }}
              />
              <Field
                component={ToggleSwitch}
                name={`${activeTab}.${index}.isTalentEditable`}
                label={t('orgSettings.talentRegistrationForm.talentEditable')}
                innerProps={{
                  sx: { pointerEvents: disabled ? 'none' : 'auto' }
                }}
              />
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export const TalentRegistrationFormQuestionDraggable = (props: TalentRegistrationFormQuestionProps) => (
  <Draggable draggableId={`${props.questionKey}-${props.activeLanguage}`} index={props.index}>
    {(provided, snapshot) => <TalentRegistrationFormQuestion provided={provided} snapshot={snapshot} {...props} />}
  </Draggable>
);
